import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { IconLoader2 } from '@tabler/icons-react'
import { ForumSeo } from '@/components/ForumSeo'
import { Button } from '@/components/ui/button'
import { apiFetch } from '@/lib/api'
import { useAuthLoading, useConfirmedGuest, useSessionUser } from '@/lib/auth'
import {
  forumAlertError,
  forumCard,
  forumCardHeader,
  forumField,
  forumKicker,
  forumLoaderRow,
  forumMutedSm,
  forumSectionBar,
  forumTitleMd,
} from '@/lib/forumUi'
import { forumPageTitle, truncateForMetaDescription } from '~shared/seo'

type Report = {
  id: string
  reporterUsername: string
  postId: string
  reasonSummary: string
  resolved: boolean
}

export function AdminPage() {
  const me = useSessionUser()
  const authLoading = useAuthLoading()
  const guest = useConfirmedGuest()

  const [reports, setReports] = useState<Report[]>([])
  const [users, setUsers] = useState<Array<{ id: string; email: string; username: string; displayName: string; role: string }>>([])
  const [error, setError] = useState<string | null>(null)

  const canMod = me?.role === 'moderator' || me?.role === 'admin'
  const canAdmin = me?.role === 'admin'

  const reload = async () => {
    try {
      setError(null)
      if (canMod) {
        const r = await apiFetch<{ reports: Report[] }>(`/api/moderation/reports`)
        setReports(r.reports ?? [])
      }

      if (canAdmin) {
        const u = await apiFetch<{ users: typeof users }>(`/api/users/internal/admin/list`)
        setUsers(u.users ?? [])
      }
    } catch {
      setError('Not authorized for this dashboard or session expired.')
    }
  }

  useEffect(() => {
    if (authLoading || guest || !me || !canMod) return
    void reload()
  }, [me, canMod, authLoading, guest])

  if (authLoading) {
    return (
      <div className={forumLoaderRow} aria-busy="true" aria-live="polite">
        <IconLoader2 className="size-5 shrink-0 animate-spin" aria-hidden="true" />
        Checking your session…
      </div>
    )
  }

  if (guest) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`}>
        <ForumSeo
          title={forumPageTitle('Moderation desk')}
          description={truncateForMetaDescription('NK Our Town moderator and admin workflows.')}
          noindex
        />
        <h1 className={forumTitleMd}>Moderation desk</h1>
        <p className={`${forumMutedSm} mt-2 text-zinc-600`}>Sign in to open moderation tools.</p>
        <Link className="mt-5 inline-block" to="/sign-in?next=/admin">
          <Button>Sign in</Button>
        </Link>
      </section>
    )
  }

  if (!me || !canMod) {
    return (
      <section className={`${forumCard} px-5 py-10 md:px-6`}>
        <ForumSeo
          title={forumPageTitle('Moderation desk')}
          description={truncateForMetaDescription('NK Our Town moderator and admin workflows.')}
          noindex
        />
        <h1 className={forumTitleMd}>Moderation desk</h1>
        <p className={`${forumMutedSm} mt-2 text-zinc-600`}>You need a moderator or admin role.</p>
      </section>
    )
  }

  return (
    <div className="space-y-6">
      <ForumSeo
        title={forumPageTitle('Moderation desk')}
        description={truncateForMetaDescription('Reports queue and moderator tools for NK Our Town.')}
        noindex
      />
      <header className={forumCard}>
        <div className={forumCardHeader}>
          <p className={forumKicker}>Staff</p>
          <h1 className={`${forumTitleMd} mt-1`}>Moderation &amp; admin</h1>
          <p className={`${forumMutedSm} mt-2 text-zinc-600`}>Reports list for moderators. User roles for admins only.</p>
        </div>
      </header>

      {error ? <div role="alert" className={forumAlertError}>{error}</div> : null}

      <section aria-labelledby="reports-heading" className={forumCard}>
        <div className={forumSectionBar}>
          <h2 id="reports-heading" className="text-sm font-semibold text-zinc-900">
            Reports
          </h2>

          <Button type="button" variant="outline" size="sm" className="rounded-lg border-zinc-200 font-semibold" onClick={() => void reload()}>
            Refresh
          </Button>
        </div>

        <div className="px-5 py-4 md:px-6 md:py-5">
          {reports.length === 0 ? (
            <p className={`${forumMutedSm} text-zinc-600`}>No open reports.</p>
          ) : (
            <ul className="list-none divide-y divide-zinc-100 rounded-xl border border-zinc-200 bg-zinc-50/40 p-0">
              {reports.map(r => (
                <li key={r.id} className="bg-white px-4 py-4 first:rounded-t-xl last:rounded-b-xl md:px-5">
                  <p className="text-[14px] font-semibold text-zinc-900">
                    From @{r.reporterUsername} · post&nbsp;
                    <code className="rounded bg-zinc-100 px-1.5 py-0.5 font-mono text-[12px] text-zinc-800">{r.postId}</code>
                  </p>

                  <p className="mt-2 text-[14px] leading-relaxed text-zinc-700">{r.reasonSummary}</p>

                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="rounded-lg border-zinc-200"
                      disabled={r.resolved}
                      aria-label={r.resolved ? 'Already resolved' : 'Mark report resolved'}
                      onClick={() =>
                        void apiFetch(`/api/moderation/reports/${encodeURIComponent(r.id)}`, {
                          method: 'PATCH',
                          json: { resolved: 'yes' },
                        }).then(() => void reload())
                      }
                    >
                      Mark resolved
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {canAdmin ? (
        <section aria-labelledby="users-heading" className={forumCard}>
          <div className={forumSectionBar}>
            <h2 id="users-heading" className="text-sm font-semibold text-zinc-900">
              Users &amp; roles
            </h2>
          </div>

          <div className="overflow-x-auto px-5 py-4 md:px-6">
            <table className="min-w-full border-collapse rounded-xl border border-zinc-200 text-left text-[13px]">
              <caption className="sr-only">Forum accounts and roles</caption>
              <thead className="border-b border-zinc-100 bg-zinc-50/90">
                <tr>
                  <th scope="col" className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                    Username
                  </th>
                  <th scope="col" className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                    Email
                  </th>
                  <th scope="col" className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                    Role
                  </th>
                  <th scope="col" className="px-3 py-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-600">
                    Change role
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100 bg-white">
                {users.map(u => (
                  <tr key={u.id}>
                    <td className="px-3 py-2 font-mono font-semibold text-zinc-900">@{u.username}</td>
                    <td className="px-3 py-2 text-zinc-700">{u.email}</td>
                    <td className="px-3 py-2 text-zinc-800">{u.role}</td>
                    <td className="px-3 py-2">
                      <label className="inline-flex flex-col gap-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-600" htmlFor={`role-${u.id}`}>
                        <span className="sr-only">Role for {u.username}</span>
                        <select
                          id={`role-${u.id}`}
                          className={`${forumField} h-auto min-h-9 py-1.5 text-[13px] font-normal normal-case`}
                          defaultValue={u.role}
                          onChange={async evt => {
                            const role = evt.target.value as 'user' | 'moderator' | 'admin'
                            await apiFetch(`/api/users/internal/admin/${encodeURIComponent(u.id)}/role`, {
                              method: 'PATCH',
                              json: { role },
                            })
                            await reload()
                          }}
                        >
                          <option value="user">user</option>
                          <option value="moderator">moderator</option>
                          <option value="admin">admin</option>
                        </select>
                      </label>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  )
}
